import type { ArenaBox, ArenaDefinition } from "@arena/shared";

/**
 * The museum at night (D49): where a room waits between matches. The players
 * come in as anonymous ghosts and take a champion by touching its statue;
 * the host keeps the room's rules and map in the curator's register. It is a wing of the same world as the
 * arena, 200 m to the south: beyond the camera's far plane (120 m), so neither
 * is ever seen from the other, and the simulation needs nothing new — only
 * its solids next to the arena's (`withMuseum`).
 */

/** The hall's inside, between the walls. */
const HALL = { minX: -13, maxX: 13, minZ: -208, maxZ: -192 } as const;
/** The back and side walls rise to the cornice; the front one is only drawn as a low rail (D52). */
const WALL_HEIGHT = { back: 8, side: 8, front: 4 } as const;
/** Pedestals along the back wall, one per champion; the ones left over wait for the next champions. */
export const MUSEUM_PEDESTALS = 12;
const PEDESTAL = { width: 1.05, height: 1.2, z: -193.3 } as const;

export interface MuseumSpot {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Where each statue stands, on top of its pedestal (index = the champion's in the roster). */
export const MUSEUM_STATUES: readonly MuseumSpot[] = Array.from({ length: MUSEUM_PEDESTALS }, (_, i) => ({
  x: -11 + 2 * i,
  y: PEDESTAL.height,
  z: PEDESTAL.z,
}));
/** The curator's desk, with the register of the room's rules (the host's). */
export const MUSEUM_DESK: MuseumSpot = { x: -11.3, y: 0, z: -200.5 };
/** Benches in the middle of the hall, and the glass cases near the gallery: solid furniture (D52). */
export const MUSEUM_BENCHES: readonly MuseumSpot[] = [{ x: -6.5, y: 0, z: -203.6 }, { x: 6.5, y: 0, z: -203.6 }];
export const MUSEUM_CASES: readonly MuseumSpot[] = [{ x: -8, y: 0, z: -197.2 }, { x: 8, y: 0, z: -197.2 }];
const BENCH = { width: 2, depth: 0.5, height: 0.45 } as const;
const CASE = { width: 1.3, depth: 0.8, height: 1.1 } as const;

/** How close a player must be to use an object. */
export const MUSEUM_REACH = { statue: 1.5, desk: 2.2 } as const;

const box = (id: string, kind: ArenaBox["kind"], min: [number, number, number], max: [number, number, number]): ArenaBox => ({
  id,
  kind,
  min: { x: min[0], y: min[1], z: min[2] },
  max: { x: max[0], y: max[1], z: max[2] },
});
/** Furniture: solid for champions, a ghost floats through it. */
const furniture = (id: string, kind: ArenaBox["kind"], min: [number, number, number], max: [number, number, number]): ArenaBox => ({ ...box(id, kind, min, max), passable: true });

export const MUSEUM: ArenaDefinition = {
  id: "museo",
  schemaVersion: 1,
  displayName: "Il museo",
  killY: -12,
  camera: { yaw: 0, pitch: 0.9, distance: 12.5 },
  solids: [
    box("museum-floor", "floor", [HALL.minX - 1, -1, HALL.minZ - 1], [HALL.maxX + 1, 0, HALL.maxZ + 1]),
    box("museum-wall-back", "wall", [HALL.minX - 1, 0, HALL.maxZ], [HALL.maxX + 1, WALL_HEIGHT.back, HALL.maxZ + 1]),
    box("museum-wall-front", "wall", [HALL.minX - 1, 0, HALL.minZ - 1], [HALL.maxX + 1, WALL_HEIGHT.front, HALL.minZ]),
    box("museum-wall-left", "wall", [HALL.minX - 1, 0, HALL.minZ], [HALL.minX, WALL_HEIGHT.side, HALL.maxZ]),
    box("museum-wall-right", "wall", [HALL.maxX, 0, HALL.minZ], [HALL.maxX + 1, WALL_HEIGHT.side, HALL.maxZ]),
    ...MUSEUM_STATUES.map((s, i) =>
      furniture(`museum-pedestal-${i}`, "pillar", [s.x - PEDESTAL.width / 2, 0, s.z - PEDESTAL.width / 2], [s.x + PEDESTAL.width / 2, PEDESTAL.height, s.z + PEDESTAL.width / 2])),
    furniture("museum-desk", "platform", [MUSEUM_DESK.x - 1.1, 0, MUSEUM_DESK.z - 0.7], [MUSEUM_DESK.x + 1.1, 1, MUSEUM_DESK.z + 0.7]),
    ...MUSEUM_BENCHES.map((b, i) =>
      furniture(`museum-bench-${i}`, "platform", [b.x - BENCH.width / 2, 0, b.z - BENCH.depth / 2], [b.x + BENCH.width / 2, BENCH.height, b.z + BENCH.depth / 2])),
    ...MUSEUM_CASES.map((c, i) =>
      furniture(`museum-case-${i}`, "pillar", [c.x - CASE.width / 2, 0, c.z - CASE.depth / 2], [c.x + CASE.width / 2, CASE.height, c.z + CASE.depth / 2])),
  ],
  // The entrance, near the front wall: everyone faces the gallery of statues.
  spawnPoints: [-3, -1, 1, 3].flatMap((x) => [{ x, y: 0, z: -205.5 }]).concat([-3, -1, 1, 3].map((x) => ({ x, y: 0, z: -206.7 }))),
};

/** Whether a point is in the museum's wing of the world (else it is in the arena's). */
export function inMuseum(p: { readonly z: number }): boolean {
  return p.z < -150;
}

const worlds = new WeakMap<ArenaDefinition, ArenaDefinition>();

/** An arena with the museum's wing: what the simulation walks on, on the server and in the prediction. */
export function withMuseum(arena: ArenaDefinition): ArenaDefinition {
  let world = worlds.get(arena);
  if (!world) {
    world = { ...arena, solids: [...arena.solids, ...MUSEUM.solids] };
    worlds.set(arena, world);
  }
  return world;
}

export type MuseumObject =
  | { readonly kind: "statue"; readonly index: number }
  | { readonly kind: "desk" };

/** The object a player at `p` can use: the nearest within reach, or null. `statues`: how many pedestals hold a champion. */
export function museumObjectAt(p: { readonly x: number; readonly z: number }, statues: number): MuseumObject | null {
  let best: MuseumObject | null = null;
  let bestDistance = Infinity;
  const consider = (object: MuseumObject, spot: MuseumSpot, reach: number): void => {
    const d = Math.hypot(p.x - spot.x, p.z - spot.z);
    if (d <= reach && d < bestDistance) {
      best = object;
      bestDistance = d;
    }
  };
  for (let i = 0; i < Math.min(statues, MUSEUM_STATUES.length); i++) consider({ kind: "statue", index: i }, MUSEUM_STATUES[i]!, MUSEUM_REACH.statue);
  consider({ kind: "desk" }, MUSEUM_DESK, MUSEUM_REACH.desk);
  return best;
}
